import React from 'react';
import { Star, Award } from 'lucide-react';
import { PlayerEvaluation } from '@/types/playerEvaluation';
import {
  EVALUATION_CRITERIA,
  TEAM_LEVELS,
  EvaluationCategory,
  TeamLevel
} from '@/types/evaluationCriteria';

interface EvaluationFormProps {
  myEvaluation: PlayerEvaluation;
  handleScoreChange: (category: EvaluationCategory, value: number) => void;
  handleNotesChange: (notes: string) => void;
  handleTeamRecommendationChange: (teamLevel: TeamLevel, selected: boolean) => void;
}

const EvaluationForm: React.FC<EvaluationFormProps> = ({
  myEvaluation,
  handleScoreChange,
  handleNotesChange,
  handleTeamRecommendationChange
}) => {
  // If there's no evaluation data (e.g., no player selected or data loading), don't render the form
  if (!myEvaluation) {
    return null; // Or return a loading indicator/placeholder
  }

  const handleTeamToggle = (teamLevel: TeamLevel) => {
    const isCurrentlySelected = !!myEvaluation.team_recommendations?.[teamLevel];
    handleTeamRecommendationChange(teamLevel, !isCurrentlySelected);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Star className="h-5 w-5 mr-2 text-yellow-500" />
          Skills Assessment
        </h3>
        {(Object.entries(EVALUATION_CRITERIA) as [EvaluationCategory, string][])
          .filter(([key]) => !key.startsWith('gk_'))
          .map(([key, label]) => (
          <div key={key} className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label}
            </label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleScoreChange(key, value)}
                  className={`w-12 h-12 rounded-lg ${
                    (myEvaluation.ratings?.[key] || 0) >= value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  } flex items-center justify-center hover:bg-blue-500 hover:text-white transition text-lg font-medium`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
            <Award className="h-5 w-5 mr-2 text-blue-600" />
            Team Recommendations
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select all team levels you recommend for this player:
            </label>
            <div className="flex flex-wrap gap-2">
              {TEAM_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => handleTeamToggle(level)}
                  className={`px-4 py-2 rounded-lg ${
                    !!myEvaluation.team_recommendations?.[level]
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  } flex items-center justify-center hover:bg-blue-500 hover:text-white transition font-medium`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={myEvaluation.notes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={4}
            placeholder="Add your observations..."
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Star className="h-5 w-5 mr-2 text-yellow-500" />
          Goalkeeper Assessment
        </h3>
        {(Object.entries(EVALUATION_CRITERIA) as [EvaluationCategory, string][])
          .filter(([key]) => key.startsWith('gk_'))
          .map(([key, label]) => (
          <div key={key} className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label}
            </label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleScoreChange(key, value)}
                  className={`w-12 h-12 rounded-lg ${
                    (myEvaluation.ratings?.[key] || 0) >= value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  } flex items-center justify-center hover:bg-blue-500 hover:text-white transition text-lg font-medium`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EvaluationForm;
